<template>
  <fieldset
    v-if="isVisible"
    ref="facetRef"
    :aria-labelledby="labelId"
    :class="{
      'is-expanded': isAccordionExpanded,
    }"
    class="product-filter"
    role="group"
  >
    <!-- Accordion Trigger for Desktop -->
    <button
      :id="labelId"
      :aria-controls="filterId"
      :aria-expanded="isAccordionExpanded"
      class="product-filter__heading u-hide-for-small"
      @click="toggleAccordion"
    >
      {{ facet.label }}
    </button>

    <!-- Accordion Trigger for Mobile -->
    <div class="product-filter__category u-hide-for-medium">
      <button
        :id="labelId"
        :aria-controls="filterId"
        :aria-expanded="isAccordionExpanded"
        class="product-filter__heading u-hide-for-medium"
        @click="toggleAccordion"
      >
        {{ facet.label }}
      </button>
      <span v-if="selectedValues?.length > 0" class="product-filter__count">
        {{ selectedValues.length }} valgt
      </span>
      <Icon 
      :type="IconTypes.ChevronDown" 
      :size="IconSizes.Tiny" 
      class="product-filter__icon"
      :class="{ 'is-rotated': isAccordionExpanded }"
      @click="toggleAccordion"/>
    </div>
    <!-- Dropdown Content -->
    <div
      v-if="isAccordionExpanded"
      :id="filterId"
      class="product-filter__content"
      :class="{
        'product-filter__content--grid':
          facet.type === FacetTypes.Color || facet.type === FacetTypes.Text,
      }"
    >
      <!-- Facet type: Color/Text -->
      <template
        v-if="facet.type === FacetTypes.Color || facet.type === FacetTypes.Text"
      >
        <label
          v-for="value in visibleColorsOrText"
          :key="value.id"
          class="checkbox product-filter__checkbox"
        >
          <input
            v-model="selectedValues"
            type="checkbox"
            :name="`facet${facet.id}`"
            :value="value.id"
            :disabled="productFilterStore.isLoading"
            @change="change()"
            @keyup.enter="clickUseFilter($event)"
          />
          <span>
            {{ value.label }} ({{ value.count }})
          </span>
          <div
            v-if="facet.type === FacetTypes.Color && value.color"
            :class="[
            `item-color`,
            getMetaColorClassName(value.label),
          ]"
            :style="{ backgroundColor: value.color }"
            :aria-label="value.label"
          ></div>
        </label>
        <span v-if="(facet.values?.length ?? 0) > NORMALFACETLIMIT" 
          class="product-filter__see-more-less u-hide-for-medium">
          <button
            class="btn--link btn--small"
            type="button"
            @click="showAllColorsOrTextFilters = !showAllColorsOrTextFilters"
          >
            {{ showAllColorsOrTextFilters ? $t("seeLess") : $t("seeMore") }}
          </button>
        </span>
        <div v-else class="product-filter__padding"></div>
      </template>

      <!-- Facet type: Size -->
      <template v-if="facet.type === FacetTypes.Size">
        <fieldset v-for="sizeType in visibleSizes" :key="sizeType.id">
          <div class="product-filter__sizes-container" v-for="format in sizeType.formats" :key="format.id">
            <!-- Suit sizes -->
            <template v-if="isSuitFormat(sizeType)">
              <label
                v-for="value in format.values"
                :key="value.id"
                class="checkbox product-filter__checkbox checkbox product-filter__checkbox--sizes"
              >
                <input
                  v-model="selectedValues"
                  type="checkbox"
                  :name="`facet${facet.id}`"
                  :value="value.id"
                  :disabled="productFilterStore.isLoading"
                  @change="change()"
                  @keyup.enter="clickUseFilter($event)"
                />
                <span>{{ value.label }} ({{ value.count }})</span>
              </label>
            </template>
            <!-- Matrix sizes -->
            <template v-else-if="isMatrixFormat(sizeType)">
              <label
                v-for="value in format.values"
                :key="value.id"
                class="checkbox product-filter__checkbox checkbox product-filter__checkbox--sizes"
              >
                <input
                  v-model="selectedValues"
                  type="checkbox"
                  :name="`facet${facet.id}`"
                  :value="value.id"
                  :disabled="productFilterStore.isLoading"
                  @change="change()"
                  @keyup.enter="clickUseFilter($event)"
                />
                <span>{{ formatFilterSize(value.label) }} ({{ value.count }})</span>
              </label>
            </template>
            <!-- Normal Sizes -->
            <template v-else>
              <label
                v-for="value in format.values"
                :key="value.id"
                class="checkbox product-filter__checkbox checkbox product-filter__checkbox--sizes"
              >
                <input
                  v-model="selectedValues"
                  type="checkbox"
                  :name="`facet${facet.id}`"
                  :value="value.id"
                  :disabled="productFilterStore.isLoading"
                  @change="change()"
                  @keyup.enter="clickUseFilter($event)"
                />
                <span>{{ formatFilterSize(value.label) }} ({{ value.count }})</span>
              </label>
            </template>
          </div>
        </fieldset>

        <!-- Show more/less button -->
        <span v-if="totalSizeValues > SIZEFACETLIMIT"
          class="product-filter__see-more-less u-hide-for-medium"
        >
          <button
            class="btn--link btn--small"
            type="button"
            @click="showAllSizesFilters = !showAllSizesFilters"
          >
            {{ showAllSizesFilters ? $t('seeLess') : $t('seeMore') }}
          </button>
        </span>
        <div v-else class="product-filter__padding"></div>
      </template>
    </div>
  </fieldset>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { IconSizes, IconTypes } from "../../components/icon/icon.model";
import Icon from "../../components/icon/icon.vue";
import {
    Facet,
    FacetTypes,
    FacetValue,
    SizeType,
} from "../../services/search.service";
import { useProductFilterStore } from "../../stores/productFilterStore";
import { getMetaColorClassName } from "../../utils/metaColor.utils";
import { formatFilterSize } from "../../utils/productSize";
import { getSelectedValueIdsFromFacet } from "../../utils/search.utils";

const props = defineProps<{
  isExpanded: boolean;
  facet: Facet;
}>();

const productFilterStore = useProductFilterStore();

const selectedValues = ref([] as Array<string>);
const initialSelectedValues = ref([] as Array<string>);
const isAccordionExpanded = ref(false); // Tracks accordion state for both desktop and mobile
const isSmallScreen = ref(window.matchMedia("(max-width: 64em)").matches);
const SIZEFACETLIMIT = 6; // Limit for the number of size values to show on small screens
const NORMALFACETLIMIT = 5; // Limit for the number of color/text values to show on small screens
const facetRef = ref<HTMLElement | null>(null);

// Always true on bigger screens
const showAllColorsOrTextFilters = ref(false);
const showAllSizesFilters = ref(false);


const handleClickOutside = (event: MouseEvent) => {
  if (
    isAccordionExpanded.value &&
    !isSmallScreen.value &&
    facetRef.value &&
    !facetRef.value.contains(event.target as Node)
  ) {
    isAccordionExpanded.value = false;
  }
}

const visibleColorsOrText = computed(() => {
  const values = props.facet.values ?? [];

  if (
    isSmallScreen.value &&
    (props.facet.type === FacetTypes.Color || props.facet.type === FacetTypes.Text) &&
    !showAllColorsOrTextFilters.value &&
    values.length > NORMALFACETLIMIT
  ) {
    return values.slice(0, NORMALFACETLIMIT);
  }

  return values;
});

const visibleSizes = computed(() => {
  if (!props.facet?.sizeTypes) return [];

  // Define a helper type for tracking position + value
  type IndexedFacetValue = {
    sizeTypeIndex: number;
    formatIndex: number;
    valueIndex: number;
    value: FacetValue;
  };

  const allValues: IndexedFacetValue[] = [];

  props.facet.sizeTypes.forEach((sizeType, sizeTypeIndex) => {
    sizeType.formats.forEach((format, formatIndex) => {
      format.values.forEach((value, valueIndex) => {
        allValues.push({
          sizeTypeIndex,
          formatIndex,
          valueIndex,
          value,
        });
      });
    });
  });

  const valuesToShow =
    !showAllSizesFilters.value && isSmallScreen.value
      ? allValues.slice(0, SIZEFACETLIMIT)
      : allValues;

  const visibleValueKeys = new Set(
    valuesToShow.map(
      (v) => `${v.sizeTypeIndex}-${v.formatIndex}-${v.valueIndex}`
    )
  );

  return props.facet.sizeTypes.map((sizeType, sizeTypeIndex) => {
  const updatedFormats = sizeType.formats
    .map((format, formatIndex) => {
      const filteredValues = format.values.filter((_, valueIndex) =>
        visibleValueKeys.has(
          `${sizeTypeIndex}-${formatIndex}-${valueIndex}`
        )
      );

      return {
        ...format,
        values: filteredValues,
      };
    })
    .filter((format) => format.values.length > 0); // <-- remove empty formats

  return {
    ...sizeType,
    formats: updatedFormats,
  };
}).filter((sizeType) => sizeType.formats.length > 0); // <-- remove empty sizeTypes
});

const totalSizeValues = computed(() => {
  if (!props.facet?.sizeTypes) return 0;

  return props.facet.sizeTypes.reduce((count, sizeType) => {
    return count + sizeType.formats.reduce((subCount, format) => {
      return subCount + format.values.length;
    }, 0);
  }, 0);
});



const handleResize = (e: MediaQueryListEvent) => {
  isSmallScreen.value = e.matches;
}

const toggleAccordion = () => {
  isAccordionExpanded.value = !isAccordionExpanded.value;
}

const emit = defineEmits<{
  (e: "expand", id: string): void;
  (
    e: "upsertFacet",
    facet: { facet: Facet; selectedValues: Array<string>; useFilter: boolean },
  );
  (e: "useFilter"): void;
}>();

const labelId = computed((): string => {
  return `facetLabel-${props.facet.id}`;
});
const filterId = computed((): string => {
  return `facetFilter-${props.facet.id}`;
});
const isVisible = computed((): boolean => {
  switch (props.facet.type) {
    case FacetTypes.Color:
      return (props.facet.values && props.facet.values.length > 0) as boolean;
    case FacetTypes.Text:
      return (props.facet.values && props.facet.values.length > 0) as boolean;
    case FacetTypes.Size:
      return (props.facet.sizeTypes &&
        props.facet.sizeTypes.length > 0) as boolean;
  }

  return false;
});
watch(
  () => props.facet,
  () => {
    selectedValues.value = getSelectedValueIdsFromFacet(props.facet);
    initialSelectedValues.value = ([] as Array<string>).concat(
      selectedValues.value,
    );
  },
  { immediate: true },
);

watch(isSmallScreen, (isSmall) => {
  showAllColorsOrTextFilters.value = !isSmall ? true : showAllColorsOrTextFilters.value;
  showAllSizesFilters.value = !isSmall ? true : showAllSizesFilters.value;
});

/**
 * Change
 */
const change = () => {
  emit("upsertFacet", {
    facet: props.facet,
    selectedValues: selectedValues.value,
    useFilter: true,
  });
}

/**
 * Click use filter
 */
const clickUseFilter = (e: Event) => {
  e.stopPropagation();

  emit("useFilter");
}

/**
 * Is suit format?
 */
const isSuitFormat = (sizeType: SizeType) => {
  return sizeType.label === "suitSize";
};

/**
 * Is matrix format?
 */
const isMatrixFormat = (sizeType: SizeType) => {
  return sizeType.label === "matrixSize";
};

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
  const mediaQuery = window.matchMedia("(max-width: 64em)");
  isSmallScreen.value = mediaQuery.matches;
  mediaQuery.addEventListener("change", handleResize);
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
  const mediaQuery = window.matchMedia("(max-width: 64em)");
  mediaQuery.removeEventListener("change", handleResize);
});

</script>
